<template>
  <GraphqlDataTable
    :query="query"
    :order-by="defaultOrderBy"
    :filter-black-list="['states', 'accounts', 'companyId']"
    :additional-query-variables="additionalQueryVariables"
    :search-placeholder="$t('Search workers...')"
    :max-height-adjustment="30"
  >
    <template #action="{ row, index }">
      <ComplianceActionsColumn :row="row" :index="index" @review="handleReview" />
    </template>
    <template #no-results>
      <NoComplianceFound>
        <template #main>{{ $t('No workers found to review yet') }}</template>
        <template #sub>
          {{
            $t('Onboard or invite candiates to review their compliance information before hiring.')
          }}
        </template>
      </NoComplianceFound>
    </template>
  </GraphqlDataTable>

  <ComplianceReviewModal
    v-if="selectedTrustedContactId"
    :show="showReviewModal"
    :trusted-contact-id="selectedTrustedContactId"
    @close="closeReviewModal"
  />
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { TypedQueryDocumentNode } from 'graphql/index'
import { graphql } from '@/gql'
import { ContactStatus, SortOrder, TrustedContactOrderByColumn } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import ComplianceActionsColumn from '@/components/GraphqlDataTable/columns/Compliance/ComplianceActionsColumn.vue'
import ComplianceReviewModal from '@/components/compliances/ComplianceReviewModal.vue'
import NoComplianceFound from '@/components/compliances/NoComplianceFound.vue'

const selectedTrustedContactId = ref<string | null>(null)
const showReviewModal = ref(false)

function handleReview(trustedContact: { id: string }) {
  selectedTrustedContactId.value = trustedContact.id
  showReviewModal.value = true
}

function closeReviewModal() {
  showReviewModal.value = false
  selectedTrustedContactId.value = null
}

const additionalQueryVariables = computed(() => ({
  accounts: [currentAccount.globalId],
  statuses: [ContactStatus.Active, ContactStatus.Invited],
}))

const query = graphql(`
  query ComplianceTrustedContactsTable(
    $accounts: [ID!]
    $page: Int!
    $first: Int!
    $search: String
    $statuses: [ContactStatus!]
    $countries: [String!]
    $orderBy: [TrustedContactOrderByClauseInput!]
    $verifiedWorkerReview: VerifiedWorkerReviewFilterInput
    $managedStatus: TrustedContactManagedStatus
  ) {
    trustedContacts(
      accounts: $accounts
      page: $page
      first: $first
      search: $search
      statuses: $statuses
      countries: $countries
      orderBy: $orderBy
      verifiedWorkerReview: $verifiedWorkerReview
      managedStatus: $managedStatus
    ) {
      data {
        ...TrustedContactInvitedOnColumn
        ...TrustedContactWorkerColumnCompact
        ...TrustedContactWorkerTypeColumn
        ...ComplianceStatusColumn
        ...ComplianceUpdatedByColumn
        ...ComplianceActionsColumn
      }
    }
  }
`) as TypedQueryDocumentNode

const defaultOrderBy = {
  field: TrustedContactOrderByColumn.WorkerDateAdded,
  order: SortOrder.Desc,
}
</script>
